/**
 * The page's locale, the catalog links built for it and the country its list
 * queries ask for (web plan W2.1).
 *
 * The locale is the URL's literal `/{cc}/{lang}` prefix (master D-11): never
 * the browser language, a cookie, the session or the IP. A page without a
 * prefix (the global pages, and every page before the URL move) belongs to
 * DEFAULT_LOCALE, the one market while Pakistan is the only launched country.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import {
  rememberedLocaleKey,
  serverRememberedLocaleKey,
  subscribeRememberedLocale,
} from '@/lib/rememberedLocale';
import { DEFAULT_LOCALE, parseLocale, type Locale } from '@/seo/locales';
import { catalogPath, isAccountPath, requestCountry, splitLocalePath } from '@/seo/urls';

/**
 * The current pathname, or null outside a `<Router>` (the data hooks are also
 * used on their own, e.g. in tests). Whether a component sits inside a router
 * never changes while it is mounted, so the hook order is stable.
 */
function useOptionalPathname(): string | null {
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks -- stable per mount (see above)
  return inRouter ? useLocation().pathname : null;
}

/**
 * The launched locale of the current URL's prefix, else DEFAULT_LOCALE. The
 * exception is a global account page (`/favorites`, `/profile`, …, web plan
 * MW6): it has no prefix, so it takes the country the visitor was browsing
 * (@/lib/rememberedLocale), DEFAULT_LOCALE's when there is none.
 */
export function useRouteLocale(): Locale {
  const pathname = useOptionalPathname();
  const remembered = useSyncExternalStore(subscribeRememberedLocale, rememberedLocaleKey, serverRememberedLocaleKey);
  const prefixed = pathname ? splitLocalePath(pathname)?.locale : undefined;
  if (prefixed) return prefixed;
  if (pathname && remembered && isAccountPath(pathname)) {
    const [cc, lang] = remembered.split('-');
    return parseLocale(cc, lang) ?? DEFAULT_LOCALE;
  }
  return DEFAULT_LOCALE;
}

/**
 * `(path) => catalogPath(locale, path)` for the page's locale: a catalog page's
 * link (`/`, `/brands`, `/search?query=…`) is today's un-prefixed path before
 * the URL move and `/pk/en/…` after it (`LOCALE_ROUTES_LIVE`). Header, footer,
 * drawer and in-page links all use it, so no link points at a URL that only
 * redirects.
 */
export function useCatalogPath(): (path?: string) => string {
  const locale = useRouteLocale();
  return useCallback((path = '/') => catalogPath(locale, path), [locale]);
}

/**
 * The `options.country` the page's list and nearby queries send (backend
 * B0.9): after the URL move, the route's literal country (`PK` on `/pk/en/…`,
 * and DEFAULT_LOCALE's on the un-prefixed pages that stay, such as an entity's
 * legacy id URL). Before it, `undefined`: the backend's default is the same
 * Pakistan, and leaving the option out keeps every request, every Apollo cache
 * key and the SEO server's cache keys exactly as they were. With it, two
 * countries never share a list's cache entry (`country` is in the list and
 * nearby `keyArgs`, @/lib/apollo).
 */
export function useRequestCountry(): string | undefined {
  return requestCountry(useRouteLocale());
}
